import { useCallback, useEffect, useState } from "react"
import { useNavigate } from "react-router-dom"
import { toast } from "react-toastify"
import loginService, {
    LoginError,
    LoginRequest
} from "../services/LoginService"

interface UseAuthReturn {
    isAuthenticated: boolean
    isLoading: boolean
    login: (credentials: LoginRequest) => Promise<void>
    logout: () => void
    error: string | null
}

export const useAuth = (): UseAuthReturn => {
    const [isAuthenticated, setIsAuthenticated] = useState(false)
    const [isLoading, setIsLoading] = useState(false)
    const [error, setError] = useState<string | null>(null)
    const navigate = useNavigate()

    // Verificar autenticação ao inicializar
    useEffect(() => {
        const checkAuth = () => {
            const authenticated = loginService.isAuthenticated()
            setIsAuthenticated(authenticated)
        }

        checkAuth()
    }, [])

    const login = useCallback(
        async (credentials: LoginRequest) => {
            setIsLoading(true)
            setError(null)

            try {
                const response = await loginService.login(credentials)
                if (response?.token) {
                    loginService.saveAuthToken(response.token)
                    setIsAuthenticated(true)

                    toast.success("Login realizado com sucesso!", {
                        style: {
                            backgroundColor: "#4caf50",
                            color: "#fff",
                            padding: "16px",
                            borderRadius: "10px",
                            border: "2px solid #388e3c",
                            fontSize: "16px"
                        }
                    })

                    setTimeout(() => {
                        navigate("/rastreamento/lote")
                    }, 100)
                } else {
                    throw {
                        message: "Resposta inválida do servidor.",
                        code: "INVALID_RESPONSE"
                    } as LoginError
                }
            } catch (error: any) {
                const loginError = error as LoginError
                setError(loginError.message)

                toast.error(loginError.message, {
                    style: {
                        backgroundColor: "#f44336",
                        color: "#fff",
                        padding: "16px",
                        borderRadius: "10px",
                        border: "2px solid #D32F2F",
                        fontSize: "16px"
                    }
                })
            } finally {
                setIsLoading(false)
            }
        },
        [navigate]
    )

    const logout = useCallback(() => {
        loginService.logout()
        setIsAuthenticated(false)
        setError(null)
        navigate("/login")

        toast.info("Logout realizado com sucesso!", {
            style: {
                backgroundColor: "#2196f3",
                color: "#fff",
                padding: "16px",
                borderRadius: "10px",
                border: "2px solid #1976d2",
                fontSize: "16px"
            }
        })
    }, [navigate])

    return {
        isAuthenticated,
        isLoading,
        login,
        logout,
        error
    }
}
